import {useCallback, useMemo, useState} from 'react';

import {Button} from '../../button/button';
import {EditableTextfieldContent} from '../../editable-textfield-content/editable-textfield-content';
import {CaretSolidDownIcon} from '../../icon';
import type {LocationEnum} from '../../popover/popover';
import {SavedSettingToast} from '../../saved-setting-toast/saved-setting-toast';
import type {SavedSettingToastState} from '../../saved-setting-toast/saved-setting-toast';
import {SelectMenu} from '../../select-menu/select-menu';
import type {SelectMenuOption} from '../../select-menu/select-menu';
import {Body} from '../../typography/body';

import styles from './renameable-row-title.module.css';

interface Props {
    title: string;
    options: SelectMenuOption[];
    onUpdateTitle: (title: string) => Promise<any>;
    location?: LocationEnum;
    isDisabled?: boolean;
    saveToastState?: SavedSettingToastState;
    setSaveToastState?: (state: SavedSettingToastState) => void;
    onDropdownOpenChange?: (isOpen: boolean) => void;
    setIsEditingTitle?: (isEditing: boolean) => void;
    isCopied?: boolean; // The timeout is controlled externally by the parent component
}

export function RenameableRowTitle(props: Props) {
    const {location = 'bottom-right', onDropdownOpenChange, setIsEditingTitle} = props;

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isRenaming, setIsRenaming] = useState<boolean>(false);
    const [dropdownAnchor, setDropdownAnchor] = useState<HTMLButtonElement | null>(null);
    const [internalSaveToastState, setInternalSaveToastState] =
        useState<SavedSettingToastState>('initial');
    const saveToastState = props.saveToastState ?? internalSaveToastState;
    const setSaveToastState = props.setSaveToastState ?? setInternalSaveToastState;

    const trackSaveToastForPromise = useCallback(
        (promise: Promise<any>) => {
            setSaveToastState('saving');
            return promise
                .then((result) => {
                    setSaveToastState('complete');
                    return result;
                })
                .catch((error) => {
                    setSaveToastState('initial');
                    throw error;
                });
        },
        [setSaveToastState]
    );

    const selectMenuOptions = useMemo(() => {
        const customOption: SelectMenuOption = {
            label: 'Custom',
            handleSelect: (_selectedOption: SelectMenuOption, _event: Event) => {
                setIsOpen(false);
                onDropdownOpenChange?.(false);
                setIsRenaming(true);
                setIsEditingTitle?.(true);
            },
        };

        const mappedOptions: SelectMenuOption[] = props.options.map((option): SelectMenuOption => {
            const optionLabel =
                typeof option.label === 'string' ? option.label.toLowerCase().trim() : '';
            const isCustomOption = optionLabel === 'custom';

            if (!isCustomOption) {
                return {
                    ...option,
                    handleSelect: (selectedOption: SelectMenuOption, event: Event) => {
                        const result = option.handleSelect?.(selectedOption, event);
                        if (!result || typeof result.then !== 'function') {
                            return result;
                        }

                        return trackSaveToastForPromise(result);
                    },
                };
            }

            return {
                ...option,
                handleSelect: (selectedOption: SelectMenuOption, event: Event) => {
                    option.handleSelect?.(selectedOption, event);
                    setIsOpen(false);
                    onDropdownOpenChange?.(false);
                    setIsRenaming(true);
                    setIsEditingTitle?.(true);
                },
            };
        });

        const hasCustomOption = mappedOptions.some((option) => {
            return (
                typeof option.label === 'string' && option.label.toLowerCase().trim() === 'custom'
            );
        });

        if (!hasCustomOption) {
            mappedOptions.push(customOption);
        }

        return mappedOptions;
    }, [props.options, trackSaveToastForPromise, onDropdownOpenChange, setIsEditingTitle]);

    const hasOptions = selectMenuOptions.length > 0;

    const handleUpdateTitle = (newValue?: string | null | undefined) => {
        const formatted = newValue ? newValue.trim() : '';
        setIsRenaming(false);
        setIsEditingTitle?.(false);
        return trackSaveToastForPromise(props.onUpdateTitle(formatted));
    };

    if (isRenaming) {
        return (
            <EditableTextfieldContent
                value={props.title}
                onUpdate={handleUpdateTitle}
                isEditing={true}
                setIsEditing={(isEditing) => {
                    setIsRenaming(isEditing);
                    setIsEditingTitle?.(isEditing);
                }}
                placeholder='Custom'
                noTopMargin={true}
                preventHideOnBlur={true}
            />
        );
    }

    if (hasOptions) {
        return (
            <>
                <div className='flex align-center'>
                    <Button
                        variant='text-secondary-dark'
                        textAlign='left'
                        className='width-fit-content max-full-width min-width-0'
                        onMouseDown={(e) => {
                            e.preventDefault();
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsOpen(true);
                            onDropdownOpenChange?.(true);
                        }}
                        getButtonRef={setDropdownAnchor}
                        disabled={props.isDisabled}
                        noPadding={true}
                    >
                        <div className='flex align-center gap-2 min-width-0 '>
                            <Body color='dark' isBold={true} wrapAnywhere={true}>
                                {props.title}
                            </Body>
                            <div className='flex-shrink'>
                                {/* @ts-expect-error */}
                                <CaretSolidDownIcon size={16} wrapWithDiv={false} color='black' />
                            </div>
                        </div>
                    </Button>
                    <div className={styles['toast-slot']}>
                        {props.isCopied && (
                            <Body size='small' wrapAnywhere={true}>
                                Copied!
                            </Body>
                        )}
                        {/* Hide toast when isCopied to prevent timer restarting on remount */}
                        <div className={props.isCopied ? 'display-none' : undefined}>
                            <SavedSettingToast
                                state={saveToastState}
                                onStateChange={setSaveToastState}
                                largerSpinner={true}
                            />
                        </div>
                    </div>
                </div>
                {isOpen && (
                    <SelectMenu
                        options={selectMenuOptions}
                        anchor={dropdownAnchor}
                        onClose={(event: Event) => {
                            event?.stopPropagation?.();
                            setIsOpen(false);
                            onDropdownOpenChange?.(false);
                        }}
                        location={location}
                        moreBorderRadius={true}
                    />
                )}
            </>
        );
    }

    return (
        <div className='flex align-center'>
            <Body color='dark' isBold={true} wrapAnywhere={true}>
                {props.title}
            </Body>
            <div className={styles['toast-slot']}>
                <SavedSettingToast
                    state={saveToastState}
                    onStateChange={setSaveToastState}
                    largerSpinner={true}
                />
            </div>
        </div>
    );
}
